import styled, { css } from 'styled-components'
import { Button } from 'sutra/components/Button'
import { ChevronDown } from 'sutra/components/Icons'
import { Popover } from 'sutra/components/Popover/Popover'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'

// `CascadingDateMenu` draws its own card (background, radius, shadow), so the
// popover's own box is stripped to a bare positioning shell around it.
export const DatePopover = styled(Popover)`
  padding: 0;
  background: transparent;
  box-shadow: none;
`

// Sutra Button's `secondary` variant already is the requested style — white at
// rest, gray[100] on hover — with no antd rule to outrank (a plain antd
// `Button` here previously required `&&` to beat antd's own hover styles,
// which still won on specificity and left the hover background/color dead).
export const TriggerButton = styled(Button).attrs({ variant: 'secondary' })<{
  $open: boolean
  /** This tile's date differs from the dashboard's — same warning
   * bg/border pair `sutra/components/Callout/Callout.tsx`'s `warning` variant uses,
   * so an unsynced tile reads visually consistent with the rest of the app. */
  $unsynced?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  height: 32px;
  min-width: 120px;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 400;
  box-shadow: none;
  border-color: ${({ $open }) => ($open ? brand.stillNight[800] : gray[300])};
  transition:
    background-color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease};

  ${({ $open }) =>
    $open &&
    css`
      /* Ties then beats the secondary variant's own
       * \`&:hover:not(:disabled)\` (specificity 0,3,0) so the open border
       * wins regardless of stylesheet injection order. */
      &&:hover:not(:disabled) {
        border-color: ${brand.stillNight[800]};
      }
    `}

  ${({ $unsynced }) =>
    $unsynced &&
    css`
      background: ${accents.goldenLight[100]};
      border-color: ${accents.goldenLight[400]};

      /* Wins over both the secondary variant's own hover rule and the
       * $open block above, regardless of stylesheet injection order. */
      &&:hover:not(:disabled) {
        background: ${accents.goldenLight[100]};
        border-color: ${accents.goldenLight[400]};
      }
    `}

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

export const TriggerChevron = styled(ChevronDown)`
  width: 12px;
  height: 12px;
  color: ${gray[500]};
`

// The "Most recent" rolling/locked status icon shown inside the trigger, next
// to the label. $clickable is only true for the locked state — rolling shows
// the icon as a plain status indicator with no action.
export const MostRecentStatusIcon = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: ${gray[500]};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

// The "this tile's dates differ from the dashboard" warning icon shown
// inside the trigger, at the far right (before the chevron) — the combined
// tooltip's third line carries the explanation. Same warning color
// `sutra/components/Callout/Callout.tsx` already uses for `WarningRoundedIcon`.
export const UnsyncedWarningIcon = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: ${accents.goldenLight[600]};
`

// The underlined "enable" affordance inside the locked icon's tooltip text.
// Rendered `as="u"` for the native underline. Clickable — the tooltip is a
// single stable block (not swapped based on exact hover position), so
// rc-tooltip keeps it open while the pointer moves from the trigger onto
// this word, same as the icon it duplicates the action of.
export const MostRecentEnableLink = styled.span`
  font-weight: 500;
  cursor: pointer;
`

// Stacks the trigger tooltip's date-range line and Most-recent status line —
// one Tooltip, two lines, rather than two tooltips that swap on hover.
export const TooltipLines = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// The tooltip's trigger. It wraps the popover rather than sitting between the
// popover and the button: the popover's trigger handlers and ref would be
// lost on a Tooltip. It also keeps hover working while the button is
// disabled. `flex` matches the button's own display so the
// wrapper doesn't change how the control lays out.
export const TooltipAnchor = styled.span`
  display: flex;
`
